import { DragDropModule, DragDropRegistry } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Inject, NgModule, NgZone, NO_ERRORS_SCHEMA, provideZonelessChangeDetection } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { RouterModule } from '@angular/router';
import {PreloadAfterStartupStrategy} from './core/services/preload-after-startup.strategy';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import { FuseModule } from '@barebone-fuse/@fuse/fuse.module';
import { fuseConfig } from '@barebone-fuse/fuse-config';
import { CoreModule } from '@core/core.module';
import { ParticleCacheService } from '@core/services/particle-cache.service';
import { SocketClusterService } from '@core/services/socket-cluster.service';
import { DataTracingModule } from '@features/data-tracing/data-tracing.module';
import { EliteFormModule } from '@features/elite-form/elite-form.module';
import { FootprintModule } from '@features/footprint/footprint.module';
import { ParticleModule } from '@features/particle.module';
import { ProjectsModule } from '@features/projects/projects.module';
import { ProjectState } from '@features/projects/store/reducers/projects.reducer';
import { TaxHubModule } from '@features/tax-hub/tax-hub.module';
import { EffectsModule } from '@ngrx/effects';
import { MinimalRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { AppLayoutComponent } from '@shared/components/app-layout/app-layout.component';
import { APlatformAppService } from '@shared/models/platform-app-service-base';
import { ItpAppService } from '@shared/services/itp-app.service';
import { HandOffReturnInterceptor } from '@core/services/hand-off-return.interceptor';
import { DemoProvisioningService } from '@shared/services/demo-provisioning.service';
import { RouterEffects } from '@shared/store/effects/router-extension.effect';
import { CustomSerializer, routerExtensionReducer } from '@shared/store/reducers/route-extension.reducers';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { ApplicationInitializer } from '../../projects/startup/src/lib/application-initializer';
import { StartupModule } from '../../projects/startup/src/lib/startup.module';
import { AppComponent } from './app.component';
import { ActionMonitorComponent } from './copilot/components/action-monitor/action-monitor.component';
import { ActionMonitorRunViewComponent } from './copilot/components/action-monitor/action-monitor-run-view/action-monitor-run-view.component';
import { XactFloatComponent } from './copilot/components/xact-float/xact-float.component';
import { RunThreadChatComponent } from './copilot/components/action-monitor/run-thread-chat/run-thread-chat.component';
import { CallsShellComponent } from '@features/calls/calls-shell/calls-shell.component';
import { appRoutes } from './app.routes';
import { appRoutesAdminpanel } from "./app.routes.adminpanel";
import { Configuration } from "@shared/models/configuration";
import { Router } from '@angular/router';
import { ContactsModule } from '@features/contacts/contacts.module';
import { Api } from '@shared/constants/common-field.constants';
import { PlatformCommand } from '@shared/commands/command-base';
import { CommandService } from '@core/services/command.service';
import { PlatformApiProvider } from '@shared/services/platform-api-provider';
import { ZoomAwareDragDropRegistry } from '@shared/helpers/zoom-aware-drag-drop-registry';
import { installChartPointerZoomFix } from '@shared/helpers/chart-pointer-zoom';
import { installSplitPointerZoomFix } from '@shared/helpers/split-pointer-zoom';
import { installKendoColumnResizeZoomFix } from '@shared/helpers/kendo-column-resize-zoom';

@NgModule({
  declarations: [
    AppComponent,
    AppLayoutComponent,
    ActionMonitorComponent,
    XactFloatComponent,
    // The run thread beside its run (XAT-E-377): the conversation a chat-started run came from, in the float's run view.
    RunThreadChatComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  imports: [
    FuseModule.forRoot(fuseConfig),
    // ITP-E-322-09: lazy modules preload once the start-up has had its time (was PreloadAllModules, at the first navigation).
    RouterModule.forRoot(appRoutes, { preloadingStrategy: PreloadAfterStartupStrategy }),
    TranslateModule.forRoot(),
    StoreModule.forRoot(routerExtensionReducer, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
        strictActionWithinNgZone: false,
        strictActionTypeUniqueness: false,
      }
    }),
    EffectsModule.forRoot([RouterEffects]),
    StoreRouterConnectingModule.forRoot({ serializer: MinimalRouterStateSerializer }),
    environment.production ? [] : StoreDevtoolsModule.instrument(),
    // Orbitax node module
    ParticleModule,
    ProjectsModule,
    MatNativeDateModule,
    CoreModule,
    CoreModule.forRoot(),
    FootprintModule,
    EliteFormModule,
    StartupModule,
    TaxHubModule,
    DataTracingModule,
    DragDropModule,
    // The Action Monitor's run view (XCT-E-12-05): the monitor's panel and the floating Xact Playground both render it.
    ActionMonitorRunViewComponent,
    ContactsModule,
    // The in-app call (ITP-E-170-01) — overlay, bar, video card, recording consent, missed-call banners — mounted
    // beside the Action Monitor; never for a magic-link-only session (ITP2-14993).
    CallsShellComponent,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (httpClient: HttpClient, securityStore: Store<SecurityState>, projectStore: Store<ProjectState>,
        cookieService: CookieService, socketClusterService: SocketClusterService,
        particleCacheService: ParticleCacheService, router: Router,
        demoProvisioningService: DemoProvisioningService) =>
        ApplicationInitializer.Init(httpClient, securityStore, projectStore,
          cookieService, socketClusterService, particleCacheService, router, environment,
          demoProvisioningService),
      deps: [HttpClient, Store, Store, CookieService, SocketClusterService, ParticleCacheService, Router,
        DemoProvisioningService],
      multi: true
    },
    // EAT-E-44-03: the hand-off return interceptor registers itself with CommandService at start-up.
    { provide: APP_INITIALIZER, useFactory: (_: HandOffReturnInterceptor) => () => undefined, deps: [HandOffReturnInterceptor], multi: true },
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    // GEN-E-100-05: cdkDrag transforms follow the cursor under the display-density zoom.
    { provide: DragDropRegistry, useClass: ZoomAwareDragDropRegistry },
    CookieService,
    FuseSplashScreenService, SocketClusterService],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private commandService: CommandService,
    private platformApiProvider: PlatformApiProvider,
    ngZone: NgZone,
    @Inject(DOCUMENT) document: Document,
  ) {
    // GEN-E-100-05: ECharts hit-tests in local px under the display-density zoom. Outside the zone,
    // because it listens to every pointer move on the page.
    ngZone.runOutsideAngular(() => installChartPointerZoomFix(document));
    // GEN-E-100-06: angular-split pixel gutters and Kendo grid column edges follow the cursor under the
    // display-density zoom.
    installSplitPointerZoomFix();
    installKendoColumnResizeZoomFix();
  }
}
